import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Map as ImmutableMap } from 'immutable';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { mcpClientsSelectors, mcpClientsUtils } from 'app-components/flux/mcp-clients';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { sharesActions, sharesSelectors, type sharesTypes } from 'app-components/flux/shares';
import { useFetchOnce } from 'app-components/hooks/utils';

import {
  DIALOG_TYPE as CONFIRMATION_DIALOG_TYPE,
  mkConfirmationDialogProps,
} from 'components/Dialogs/ConfirmationDialog';
import QButton, { QButtonVariantEnum } from 'components/QButton';
import SettingsHeader from 'containers/Settings/SharedComponents/SettingsHeader';

const INACTIVE_STATE = 'INACTIVE';

type ConnectedAiRowProps = {
  share: sharesTypes.Share;
  isHighlighted: boolean;
  onDisconnect: (share: sharesTypes.Share) => void;
};

const ConnectedAiRow = ({ share, isHighlighted, onDisconnect }: ConnectedAiRowProps) => {
  const product = mcpClientsUtils.getMcpClientProduct(share.clientId);

  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      spacing={2}
      sx={{ px: 2, py: 1.5, backgroundColor: isHighlighted ? 'action.hover' : undefined }}
    >
      <Box>
        <Typography variant='bodyMedium'>{product}</Typography>
        <Typography variant='captionRegular' color='text.secondary'>
          Has read access to this space
        </Typography>
      </Box>
      <QButton variant={QButtonVariantEnum.OUTLINED} onClick={() => onDisconnect(share)}>
        Disconnect
      </QButton>
    </Stack>
  );
};

export const ConnectedAiSettings = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const highlightedClientId = searchParams.get('client');

  useFetchOnce(sharesActions.getShares, sharesSelectors, true);

  const mcpShares = useSelector(mcpClientsSelectors.getMcpShares);

  const handleDisconnect = useCallback(
    (share: sharesTypes.Share) => {
      const product = mcpClientsUtils.getMcpClientProduct(share.clientId);
      dispatch(
        rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            id: 'disconnect-ai-client',
            type: CONFIRMATION_DIALOG_TYPE,
            props: ImmutableMap(
              mkConfirmationDialogProps({
                title: 'Disconnect',
                content: `After disconnecting, ${product} will no longer have access to this space.`,
                confirmLabel: 'Continue',
                denyLabel: 'Cancel',
                confirmAction: () => dispatch(sharesActions.updateShare(share.set('state', INACTIVE_STATE))),
              }),
            ),
          }),
        ),
      );
    },
    [dispatch],
  );

  return (
    <Box>
      <SettingsHeader
        title='Connected AI'
        description='AI assistants you have connected to this space. Each one has its own access and can be disconnected independently.'
      />
      <Paper variant='outlined' sx={{ mt: 2 }}>
        {mcpShares.length === 0 ? (
          <Typography variant='captionRegular' color='text.secondary' sx={{ px: 2, py: 3 }}>
            No AI assistants are connected to this space.
          </Typography>
        ) : (
          <Stack divider={<Divider />}>
            {mcpShares.map((share) => (
              <ConnectedAiRow
                key={share.id}
                share={share}
                isHighlighted={Boolean(highlightedClientId) && share.clientId === highlightedClientId}
                onDisconnect={handleDisconnect}
              />
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export const Component = ConnectedAiSettings;
export default ConnectedAiSettings;
